import React, { createRef, forwardRef, JSX, Ref, RefObject, useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import Keys from '../../../Translations/generated/date-range-input.en.json.keys';
import DataTestIdProp from '../DataTestIdProp';
import { CustomDisplayProps, OnChangeDate } from './CustomDisplayProps';

interface KeyboardInputConfig {
  inputValue: string;
  format: string | undefined;
  minDate: Date | null | undefined;
  maxDate: Date | null | undefined;
  invalidDateMessage: string;
  invalidMinDateMessage: string;
  invalidMaxDateMessage: string;
}

interface KeyboardValidationResult {
  message: string;
  date: Date | undefined;
}

const validateKeyboardInput = (config: KeyboardInputConfig): KeyboardValidationResult => {
  let message = '';
  let date: Date | undefined = undefined;

  const dayJsParsed = dayjs.utc(config.inputValue, config.format, true);

  // first check if entered date is valid to the given format
  if (dayJsParsed.isValid()) {
    date = dayJsParsed.toDate();
  } else {
    message = config.invalidDateMessage;
  }

  // validate minDate if set
  if (config.minDate && dayjs(config.minDate).isAfter(dayJsParsed)) {
    message = config.invalidMinDateMessage;
    date = undefined;
  }

  // validate maxDate if set
  if (config.maxDate && dayjs(config.maxDate).isBefore(dayJsParsed)) {
    message = config.invalidMaxDateMessage;
    date = undefined;
  }

  return { message, date };
};

export const DateWithKeyboardSupportInner = (
  props: CustomDisplayProps & DataTestIdProp,
  ref: Ref<Button>
): JSX.Element => {
  const {
    onClick,
    onInputChange,
    date,
    format,
    placeholder,
    dataTestId,
    minDate,
    maxDate,
    inputClassName,
    useFormValidation,
    setValueToNullWhenFormatIsIncorrect,
    noInitialValueInput
  } = props;
  const inputRef: RefObject<HTMLInputElement | null> = createRef();

  const [isInitialized, setIsInitialized] = useState(!noInitialValueInput);

  useEffect(() => {
    if (isInitialized && date) {
      if (inputRef?.current) {
        inputRef.current.value = dayjs(date).format(format);
      }
    }
  }, [date, isInitialized]);

  const [errorMsg, setErrorMsg] = useState('');

  const { t } = useTranslation('date-range-input');

  const defaultInitialValue = date ? dayjs(date).format(format) : '';

  return (
    <div data-testid={dataTestId}>
      <div className="d-flex">
        <div className="mr-2 flex-column flex-grow-1">
          <input
            type="text"
            aria-label={placeholder}
            className={`iso--date-picker--input  ${inputClassName ?? ''}`}
            placeholder={placeholder}
            defaultValue={noInitialValueInput ? '' : defaultInitialValue}
            ref={inputRef}
            onBlur={val => {
              setIsInitialized(true);
              if (!val.target.value || val.target.value.trim() === '') {
                return;
              }

              if (!!onInputChange && setValueToNullWhenFormatIsIncorrect) {
                const parsedDayJs = dayjs.utc(val.target.value, format, true);
                if (!parsedDayJs.isValid()) {
                  onInputChange(null);
                }
              }

              // using form validation (DatePickerWrapper & FormDatePicker) will skip DatePicker's own validation
              if (!!onInputChange && useFormValidation) {
                const parsedDayJs = dayjs.utc(val.target.value, format, true);
                if (parsedDayJs.isValid()) {
                  onInputChange(parsedDayJs.toDate() as OnChangeDate);
                }
                return;
              }

              const checkInputValidity = validateKeyboardInput({
                inputValue: val.target.value,
                format,
                minDate,
                maxDate,
                invalidDateMessage: t(
                  format ? Keys.invalidDateWithFormat : Keys.invalidDateFormat,
                  {
                    format: format
                  }
                ),
                invalidMinDateMessage: t('Keys.invalidMinDate', {
                  minDate: dayjs(minDate).format(format)
                }),
                invalidMaxDateMessage: t('Keys.invalidMaxDate', {
                  maxDate: dayjs(maxDate).format(format)
                })
              });

              if (!!onInputChange && checkInputValidity.message === '' && checkInputValidity.date) {
                setErrorMsg('');
                onInputChange(checkInputValidity.date as OnChangeDate);
              }

              if (checkInputValidity.message !== '') {
                setErrorMsg(checkInputValidity.message);
              }
            }}
          />
        </div>
        <Button
          onClick={() => {
            setIsInitialized(true);
            if (onClick) {
              onClick();
            }
          }}
          ref={ref}
          className="iso__date-picker__keyboardSupportButton"
          aria-label={t('Keys.ARIA_LABEL_datePickerButton')}
        >
          <FontAwesomeIcon icon={faCalendarAlt} />
        </Button>
      </div>
      {!useFormValidation && <span className="validation-error">{errorMsg}</span>}
    </div>
  );
};

export const DateWithKeyboardSupport = forwardRef(DateWithKeyboardSupportInner);
